/**
 * Subscription Paywall — Full-screen gate for users without an active subscription.
 *
 * EASI has NO free tier. All access requires a valid subscription
 * purchased through Northern Speech Services.
 *
 * Mounts only on a positive no-subscription signal from /subscriptions/me
 * (200 with hasSubscription:false, or 404). Transient API failures route to
 * the SubscriptionUnavailable screen instead — see App.tsx.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Box, Button, Typography, Paper, CircularProgress } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../../hooks/useAuth';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { getAuthSession } from '../../auth/cognito-client';
import { post } from '../../api/client';

const NSS_PURCHASE_URL = 'https://www.northernspeech.com/4a03/';

export default function SubscriptionPaywall() {
  const { signOut } = useAuth();
  const { refresh, error: subError } = useSubscription();
  const [refreshing, setRefreshing] = useState(false);

  // Regression detector. After the 2026-05-02 paywall lockout fix, the paywall
  // mounts only on a positive no-subscription signal — subError must be null.
  // If a future change re-introduces the bug and surfaces the paywall on a
  // transient error, this metric fires so we see it before customers do.
  useEffect(() => {
    if (subError) {
      void post('/subscriptions/client-error', {
        event: 'SubscriptionPaywallShownOnError',
        message: subError,
        path: typeof window !== 'undefined' ? window.location.pathname : '',
      }).catch(() => { /* fire-and-forget */ });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only

  // Refresh Status: try a real Cognito token refresh BEFORE re-fetching
  // /subscriptions/me. If the refresh fails, route to /login rather than
  // looping on the same expired-token wall. If it succeeds, re-fetch.
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const session = await getAuthSession(true);
      if (!session?.idToken) {
        if (typeof window !== 'undefined') {
          window.location.href = '/login?session_expired=true';
        }
        return;
      }
      await refresh();
    } finally {
      setRefreshing(false);
    }
  }, [refresh]);

  const handleSignOut = useCallback(async () => {
    await signOut();
  }, [signOut]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{ background: 'linear-gradient(135deg, #F0FDF4 0%, #FDF8F4 50%, #EFF6FF 100%)' }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 440,
          width: '90%',
          p: 4,
          borderRadius: 3,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '50%',
            padding: '4px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            boxShadow: '0 4px 20px rgba(45, 125, 70, 0.15)',
          }}
        >
          <Box
            component="img"
            src="/easi-logo.png"
            alt="EASI"
            sx={{ width: 64, height: 64, borderRadius: '50%', display: 'block' }}
          />
        </Box>

        <Typography
          variant="h5"
          sx={{ fontWeight: 800, color: '#2D7D46', mb: 1 }}
        >
          Welcome to EASI
        </Typography>

        <Typography
          variant="body1"
          sx={{ color: '#4B5563', mb: 3, lineHeight: 1.6 }}
        >
          To access the platform, you need an active subscription from
          Northern Speech Services.
        </Typography>

        <Button
          variant="contained"
          size="large"
          fullWidth
          href={NSS_PURCHASE_URL}
          target="_blank"
          rel="noopener noreferrer"
          endIcon={<OpenInNewIcon />}
          sx={{
            mb: 3,
            py: 1.5,
            background: 'linear-gradient(135deg, #2D7D46, #22C55E)',
            fontWeight: 700,
            fontSize: '1rem',
            textTransform: 'none',
            borderRadius: 2,
            '&:hover': {
              background: 'linear-gradient(135deg, #256B3B, #16A34A)',
            },
          }}
        >
          Purchase Subscription
        </Button>

        <Typography
          variant="body2"
          sx={{ color: '#6B7280', mb: 2 }}
        >
          Already purchased? Your subscription may take a few minutes to activate.
        </Typography>

        <Button
          variant="outlined"
          onClick={handleRefresh}
          disabled={refreshing}
          startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
          sx={{
            mb: 2,
            textTransform: 'none',
            borderColor: '#2D7D46',
            color: '#2D7D46',
            '&:hover': { borderColor: '#256B3B', backgroundColor: 'rgba(45, 125, 70, 0.04)' },
          }}
        >
          {refreshing ? 'Checking...' : 'Refresh Status'}
        </Button>

        <Box>
          <Button
            variant="text"
            onClick={handleSignOut}
            startIcon={<LogoutIcon />}
            sx={{
              textTransform: 'none',
              color: '#6B7280',
              '&:hover': { color: '#374151' },
            }}
          >
            Sign Out
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
